// ============================================================================
// COMPONENTE: Dialog para visualizar histórico do paciente
// ============================================================================
// Funcionalidades:
// - Exibir informações do paciente
// - Mostrar estatísticas (atendimentos, próxima agenda, última visita)
// - Listar histórico de procedimentos por agendamento
// - Expandir/colapsar detalhes de cada atendimento
// - Design responsivo (Desktop 2 colunas, Mobile 1 coluna)
// ============================================================================

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useState, useEffect } from "react";
import {
  FaUser,
  FaChartBar,
  FaFileMedical,
  FaCalendarPlus,
  FaCalendarCheck,
  FaTooth,
  FaHistory,
  FaUserMd,
  FaMapMarkerAlt,
  FaTag,
  FaCalendarAlt,
  FaUserFriends,
  FaClock,
  FaBuilding,
  FaStickyNote,
  FaList,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const DialogHistoricoPaciente = ({
  visible,
  onHide,
  pacienteEscolhido,
  reservaEditando,
  fetchPacientes,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [itensExpandidos, setItensExpandidos] = useState([]);

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // FUNÇÃO: Alternar detalhes do agendamento
  // ==========================================================================
  const alternarDetalhes = (id) => {
    setItensExpandidos((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  // ==========================================================================
  // FUNÇÃO: Formatar data
  // ==========================================================================
  const formatarData = (dataStr) => {
    if (!dataStr) return "-";
    try {
      const data = new Date(dataStr);
      return data.toLocaleDateString("pt-BR");
    } catch (e) {
      return dataStr;
    }
  };

  // ==========================================================================
  // FUNÇÃO: Formatar horário
  // ==========================================================================
  const formatarHorario = (horario) => {
    if (!horario) return "-";
    return horario.substring(0, 5);
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  const labelStyle = {
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "0.85rem",
    letterSpacing: "0.3px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      style={{
        width: "90vw",
        maxWidth: "900px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      onHide={onHide}
      draggable={false}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaUserFriends style={{ fontSize: "2.4rem", color: "#B83732" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                HISTÓRICO DO
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                PACIENTE
              </div>
            </div>
          </div>
        </div>
      }
    >
      {pacienteEscolhido && reservaEditando && (
        <>
          {/* CARD ÚNICO */}
          <div
            style={{
              backgroundColor: "#FAFAFA",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {/* ========== SEÇÃO 1: INFORMAÇÕES DO PACIENTE ========== */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {/* Card Informações do Paciente */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaUser style={iconStyle} />
                  <span>INFORMAÇÕES DO PACIENTE</span>
                </div>
                <div
                  style={{
                    fontSize: "0.9rem",
                    color: "#4a5568",
                    marginTop: "8px",
                  }}
                >
                  <div style={{ marginBottom: "4px" }}>
                    <strong>Nome:</strong> {reservaEditando.paciente_nome}
                  </div>
                  <div style={{ marginBottom: "4px" }}>
                    <strong>CPF:</strong> {reservaEditando.paciente_cpf}
                  </div>
                  <div style={{ marginBottom: "4px" }}>
                    <strong>Telefone:</strong>{" "}
                    {reservaEditando.paciente_telefone || "---"}
                  </div>
                  <div style={{ marginBottom: "4px" }}>
                    <strong>Email:</strong>{" "}
                    {reservaEditando.paciente_email || "---"}
                  </div>
                </div>
              </div>

              {/* Card Estatísticas */}
              <div
                style={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <div style={labelStyle}>
                  <FaChartBar style={iconStyle} />
                  <span>ESTATÍSTICAS</span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "12px",
                    marginTop: "8px",
                  }}
                >
                  {/* Atendimentos */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <FaFileMedical
                      style={{ color: "#3498db", fontSize: "1.2rem" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        ATENDIMENTOS
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          color: "#3498db",
                        }}
                      >
                        {
                          new Set(
                            pacienteEscolhido
                              .filter(
                                (p) =>
                                  p.status === "confirmado" ||
                                  p.status === "realizado",
                              )
                              .map((p) => p.agendamento_id),
                          ).size
                        }
                      </div>
                    </div>
                  </div>

                  {/* Próxima Agenda */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <FaCalendarPlus
                      style={{ color: "#2ecc71", fontSize: "1.2rem" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        PRÓXIMA AGENDA
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          color: "#2ecc71",
                        }}
                      >
                        {(() => {
                          const confirmados = pacienteEscolhido.filter(
                            (item) => item.status === "confirmado",
                          );
                          if (!confirmados.length) return "-";
                          const maisRecente = confirmados.reduce(
                            (prev, curr) =>
                              new Date(curr.data_atendimento) <
                              new Date(prev.data_atendimento)
                                ? curr
                                : prev,
                          );
                          return formatarData(maisRecente.data_atendimento);
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Última Visita */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <FaCalendarCheck
                      style={{ color: "#f39c12", fontSize: "1.2rem" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        ÚLTIMA VISITA
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          color: "#f39c12",
                        }}
                      >
                        {pacienteEscolhido.filter(
                          (p) => p.status === "realizado",
                        ).length > 0
                          ? formatarData(
                              pacienteEscolhido.filter(
                                (p) => p.status === "realizado",
                              )[0].data_realizacao,
                            )
                          : "-"}
                      </div>
                    </div>
                  </div>

                  {/* Último Procedimento */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "10px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <FaTooth style={{ color: "#e74c3c", fontSize: "1.2rem" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                        ÚLT. PROCEDIMENTO
                      </div>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          color: "#e74c3c",
                        }}
                      >
                        {pacienteEscolhido.filter(
                          (p) => p.status === "realizado",
                        ).length > 0
                          ? pacienteEscolhido.filter(
                              (p) => p.status === "realizado",
                            )[0].dentes || "-"
                          : "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== SEÇÃO 2: HISTÓRICO DE PROCEDIMENTOS ========== */}
            <div>
              <div style={labelStyle}>
                <FaHistory style={iconStyle} />
                <span>HISTÓRICO DE PROCEDIMENTOS</span>
              </div>

              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  marginTop: "12px",
                }}
              >
                {pacienteEscolhido.filter(
                  (procedimento) => procedimento.status === "realizado",
                ).length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      backgroundColor: "white",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                  >
                    <FaUser
                      style={{
                        fontSize: "2.5rem",
                        color: "#cbd5e1",
                        marginBottom: "10px",
                      }}
                    />
                    <div style={{ fontSize: "0.9rem", color: "#94a3b8" }}>
                      Nenhum Histórico encontrado para este paciente
                    </div>
                  </div>
                ) : (
                  // Agrupa os procedimentos por agendamento_id
                  Object.values(
                    pacienteEscolhido
                      .filter(
                        (procedimento) => procedimento.status === "realizado",
                      )
                      .reduce((agrupados, procedimento) => {
                        const agendamentoId = procedimento.agendamento_id;

                        if (!agrupados[agendamentoId]) {
                          agrupados[agendamentoId] = {
                            agendamento_id: agendamentoId,
                            data_realizacao: procedimento.data_realizacao,
                            data_atendimento: procedimento.data_atendimento,
                            profissional_nome: procedimento.profissional_nome,
                            empresa_nome: procedimento.empresa_nome,
                            queixas: procedimento.queixas,
                            servico_descricao: procedimento.servico_descricao,
                            procedimento_observacoes:
                              procedimento.procedimento_observacoes,
                            servicos_extras: procedimento.servicos_extras || [],
                            dentes: [],
                            procedimentos: [],
                          };
                        }

                        if (procedimento.dentes) {
                          agrupados[agendamentoId].dentes.push(
                            procedimento.dentes,
                          );
                        }

                        agrupados[agendamentoId].procedimentos.push({
                          procedimento_id: procedimento.procedimento_id,
                          dentes: procedimento.dentes,
                          condicao: procedimento.condicao,
                          procedimento_descricao:
                            procedimento.procedimento_descricao,
                        });

                        if (
                          procedimento.servicos_extras &&
                          procedimento.servicos_extras.length > 0
                        ) {
                          procedimento.servicos_extras.forEach(
                            (servicoExtra) => {
                              const existe = agrupados[
                                agendamentoId
                              ].servicos_extras.some(
                                (s) => s.id === servicoExtra.id,
                              );
                              if (!existe) {
                                agrupados[agendamentoId].servicos_extras.push(
                                  servicoExtra,
                                );
                              }
                            },
                          );
                        }

                        return agrupados;
                      }, {}),
                  ).map((agendamento, index) => {
                    const isExpandido = itensExpandidos.includes(
                      `agendamento_${agendamento.agendamento_id}`,
                    );

                    return (
                      <div
                        key={`agendamento_${agendamento.agendamento_id || index}`}
                        style={{
                          backgroundColor: "white",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          marginBottom: "12px",
                          overflow: "hidden",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Cabeçalho do Agendamento */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 15px",
                            backgroundColor: "#f8fafc",
                            cursor: "pointer",
                            borderBottom: isExpandido
                              ? "1px solid #e2e8f0"
                              : "none",
                          }}
                          onClick={() =>
                            alternarDetalhes(
                              `agendamento_${agendamento.agendamento_id}`,
                            )
                          }
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "0.85rem",
                                color: "#4a5568",
                              }}
                            >
                              <FaCalendarAlt
                                style={{ color: "#94a3b8", fontSize: "0.7rem" }}
                              />
                              <span style={{ fontWeight: "500" }}>
                                {formatarData(
                                  agendamento.data_realizacao ||
                                    agendamento.data_atendimento,
                                )}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "0.85rem",
                                color: "#4a5568",
                              }}
                            >
                              <FaTag
                                style={{ color: "#94a3b8", fontSize: "0.7rem" }}
                              />
                              <span style={{ fontWeight: "500" }}>
                                {agendamento.agendamento_id}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "0.85rem",
                                color: "#4a5568",
                              }}
                            >
                              <FaUserMd
                                style={{ color: "#94a3b8", fontSize: "0.7rem" }}
                              />
                              <span style={{ fontWeight: "500" }}>
                                {agendamento.profissional_nome}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "0.85rem",
                                color: "#4a5568",
                              }}
                            >
                              <FaBuilding
                                style={{ color: "#94a3b8", fontSize: "0.7rem" }}
                              />
                              <span style={{ fontWeight: "500" }}>
                                {agendamento.empresa_nome}
                              </span>
                            </div>
                          </div>

                          <div>
                            {isExpandido ? (
                              <FaChevronUp
                                style={{ color: "#B83732", fontSize: "0.8rem" }}
                              />
                            ) : (
                              <FaChevronDown
                                style={{ color: "#B83732", fontSize: "0.8rem" }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Detalhes Expandidos */}
                        {isExpandido && (
                          <div
                            style={{
                              padding: "15px",
                              backgroundColor: "#ffffff",
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            {/* Serviço Principal */}
                            <div style={{ marginBottom: "12px" }}>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#64748b",
                                  marginBottom: "4px",
                                  fontWeight: "600",
                                }}
                              >
                                SERVIÇO PRINCIPAL
                              </div>
                              <div
                                style={{
                                  backgroundColor: "#e8f5e9",
                                  padding: "8px 12px",
                                  borderRadius: "6px",
                                  fontSize: "0.85rem",
                                  fontWeight: "500",
                                  color: "#2c3e50",
                                }}
                              >
                                {agendamento.servico_descricao || "-"}
                              </div>
                            </div>

                            {/* Serviços Extras */}
                            {agendamento.servicos_extras &&
                              agendamento.servicos_extras.length > 0 && (
                                <div style={{ marginBottom: "12px" }}>
                                  <div
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#64748b",
                                      marginBottom: "4px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    SERVIÇOS EXTRAS
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    {agendamento.servicos_extras.map(
                                      (servico, idx) => (
                                        <div
                                          key={servico.id || idx}
                                          style={{
                                            padding: "8px 12px",
                                            borderBottom:
                                              idx <
                                              agendamento.servicos_extras
                                                .length -
                                                1
                                                ? "1px solid #e2e8f0"
                                                : "none",
                                          }}
                                        >
                                          <div
                                            style={{
                                              fontWeight: "500",
                                              fontSize: "0.85rem",
                                            }}
                                          >
                                            {servico.descricao}
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Queixas */}
                            {agendamento.queixas && (
                              <div style={{ marginBottom: "12px" }}>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#64748b",
                                    marginBottom: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  QUEIXAS
                                </div>
                                <div
                                  style={{
                                    backgroundColor: "#fff8e1",
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    fontSize: "0.85rem",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {agendamento.queixas}
                                </div>
                              </div>
                            )}

                            {/* Procedimentos por Dente */}
                            <div style={{ marginBottom: "12px" }}>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#64748b",
                                  marginBottom: "4px",
                                  fontWeight: "600",
                                }}
                              >
                                PROCEDIMENTOS REALIZADOS
                              </div>
                              {agendamento.procedimentos.map((proc, idx) => (
                                <div
                                  key={proc.procedimento_id || idx}
                                  style={{
                                    backgroundColor: "#f8fafc",
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    marginBottom: "8px",
                                    border: "1px solid #e2e8f0",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <FaTooth
                                      style={{
                                        color: "#B83732",
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                    <span
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {proc.dentes || "Nenhum dente informado"}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#4a5568",
                                      marginLeft: "20px",
                                    }}
                                  >
                                    {proc.procedimento_descricao ||
                                      proc.condicao ||
                                      "Sem descrição informada"}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Observações */}
                            {agendamento.procedimento_observacoes && (
                              <div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: "#64748b",
                                    marginBottom: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  OBSERVAÇÕES
                                </div>
                                <div
                                  style={{
                                    backgroundColor: "#fff8e1",
                                    padding: "8px 12px",
                                    borderRadius: "6px",
                                    fontSize: "0.85rem",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {agendamento.procedimento_observacoes}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Botão Fechar */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              marginTop: "24px",
            }}
          >
            <Button
              label="FECHAR"
              icon="pi pi-times"
              onClick={onHide}
              className="p-button-outlined"
              style={{
                padding: "10px 28px",
                minWidth: "120px",
                borderColor: "#e2e8f0",
                color: "#718096",
                fontWeight: "600",
                borderRadius: "8px",
              }}
            />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default DialogHistoricoPaciente;
